import { type FC } from 'preact/compat';
import { useCallback } from 'preact/hooks';

import clsx from 'clsx';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';

import { GameOrientation } from '@/shared/api/game/types';
import { useShare } from '@/shared/hooks/use-share/use-share';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { makeShareEvent } from '@/shared/service/analytics/events/click-share-game-event';
import { useAppState } from '@/shared/stores/app-store/app-store';
import { ButtonSize, ButtonVariant } from '@/shared/ui/button/types';
import { Button } from '@/shared/ui/button/ui';

import { ReactComponent as ShareIcon } from '@/assets/images/icons/share.svg?react';

import s from './index.module.scss';

interface Props {
  className?: string;
  place: 'bar' | 'start';
}

export const ShareButton: FC<Props> = ({ className, place = 'start' }) => {
  const { screenOrientation } = useAppState();

  const t = useIntl();
  const share = useShare(place);
  const { game, lang } = useAppState();

  const handleShareClick = useCallback(() => {
    if (!game?.key) return;

    const e = makeShareEvent(place, game?.key);
    AnalyticsService.send(e);

    share();
  }, [share, game?.key, place]);

  const isPortrait = screenOrientation === GameOrientation.PORTRAIT;

  if (place === 'bar') {
    return (
      <Button
        className={clsx(s.container, s['bar-button'], className)}
        size={ButtonSize.s30}
        icon={ShareIcon}
        media
        onClick={handleShareClick}
      />
    );
  }

  return (
    <Button
      className={clsx(s.container, s[lang.app], className)}
      variant={ButtonVariant.secondary}
      icon={ShareIcon}
      onClick={handleShareClick}
    >
      {(!game?.fight || isPortrait) && t('Share')}
    </Button>
  );
};
